import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    increment,
    runTransaction,
    query,
    orderBy,
    limit,
    where,
    arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';
import PiAdapter from '../adapters/PiAdapter';
import { approvePayment, completePayment, editSpot, deleteSpot, getAPIUrls } from './api';

// 🎯 [Helper] Environment-aware Collection Path
const getCollectionPath = (base, env) => {
    if (!env || env === 'production' || env === 'mainnet') return base;
    // 🎯 [Revert] Sandbox uses root collections (user feedback: data exists in root)
    if (env === 'sandbox') return base;
    if (env === 'testnet') return `testnet_${base}`;
    return `dev_${base}`;
};

export const SpotService = {

    /**
     * Toggle Like with Duplicate Prevention (Subcollection Strategy)
     */
    /**
     * Toggle Like via Cloud Function
     */
    toggleLike: async (spotId, userId, environment) => {
        if (!userId) throw new Error("로그인이 필요합니다.");

        // Direct DB Transaction for Performance
        const spotsCol = getCollectionPath("spots", environment);
        const spotRef = doc(db, spotsCol, spotId);
        const likeRef = doc(db, spotsCol, spotId, "likes", userId);

        let newLikedState = false;

        await runTransaction(db, async (transaction) => {
            const likeDoc = await transaction.get(likeRef);
            if (likeDoc.exists()) {
                // Unlike
                transaction.delete(likeRef);
                transaction.update(spotRef, {
                    likeCount: increment(-1),
                    lastActiveAt: serverTimestamp()
                });
                newLikedState = false;
            } else {
                // Like
                transaction.set(likeRef, {
                    userId,
                    createdAt: serverTimestamp()
                });
                transaction.update(spotRef, {
                    likeCount: increment(1),
                    lastActiveAt: serverTimestamp()
                });
                newLikedState = true;
            }
        });

        return newLikedState;
    },

    /**
     * Check if user liked a specific spot
     */
    checkIsLiked: async (spotId, userId, environment) => {
        if (!userId) return false;
        try {
            const spotsCol = getCollectionPath("spots", environment);
            const likeRef = doc(db, spotsCol, spotId, "likes", userId);
            const snap = await getDoc(likeRef);
            return snap.exists();
        } catch (e) {
            console.error(e);
            return false;
        }
    },

    /**
     * Get Spot By ID (Deep Link Support)
     */
    getSpotById: async (spotId, environment) => {
        try {
            const spotsCol = getCollectionPath("spots", environment);
            const spotRef = doc(db, spotsCol, spotId);
            const snap = await getDoc(spotRef);
            if (snap.exists()) {
                return { id: snap.id, ...snap.data() };
            }
            return null;
        } catch (e) {
            console.error("SpotService: getSpotById failed", e);
            throw e;
        }
    },

    /**
     * Add Comment
     */
    /**
     * Add Comment (Direct Firestore)
     * 🎯 [Fix] Update 'commentedByUsers' for My Comments filter
     */
    addComment: async (spotId, userId, userName, text, environment) => {
        if (!userId) throw new Error("로그인이 필요합니다.");
        if (!text.trim()) throw new Error("댓글 내용을 입력해주세요.");

        const spotsCol = getCollectionPath("spots", environment);
        const spotRef = doc(db, spotsCol, spotId);

        // 1. Add Comment Document
        const commentData = {
            userId,
            userName,
            text: text.trim(),
            createdAt: serverTimestamp()
        };

        const commentsRef = collection(db, spotsCol, spotId, "comments");
        const docRef = await addDoc(commentsRef, commentData);

        // 2. Update Spot Stats (Atomic)
        await updateDoc(spotRef, {
            commentCount: increment(1),
            commentedByUsers: arrayUnion(userId), // 🎯 필터링을 위한 필드 추가
            lastActiveAt: serverTimestamp()
        });

        return { id: docRef.id, ...commentData };
    },

    /**
     * Get Comments (Simple Fetch)
     */
    getComments: async (spotId, environment) => {
        try {
            const spotsCol = getCollectionPath("spots", environment);
            const q = query(collection(db, spotsCol, spotId, "comments"), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
            console.error("SpotService: getComments failed", e);
            return [];
        }
    },

    /**
     * 🎯 [NEW] Delete Comment (Client-side)
     */
    deleteComment: async (spotId, commentId, userId, environment) => {
        if (!userId) throw new Error("로그인이 필요합니다.");

        const spotsCol = getCollectionPath("spots", environment);
        const commentRef = doc(db, spotsCol, spotId, "comments", commentId);

        // 권한 체크
        const commentSnap = await getDoc(commentRef);
        if (!commentSnap.exists()) throw new Error("댓글을 찾을 수 없습니다.");
        if (commentSnap.data().userId !== userId) throw new Error("삭제 권한이 없습니다.");

        await deleteDoc(commentRef);

        // commentCount 감소
        const spotRef = doc(db, spotsCol, spotId);
        await updateDoc(spotRef, { commentCount: increment(-1) });

        return { success: true };
    },

    /**
     * 🎯 [NEW] Edit Comment (Client-side)
     */
    editComment: async (spotId, commentId, userId, newText, environment) => {
        if (!userId) throw new Error("로그인이 필요합니다.");
        if (!newText.trim()) throw new Error("댓글 내용을 입력해주세요.");

        const spotsCol = getCollectionPath("spots", environment);
        const commentRef = doc(db, spotsCol, spotId, "comments", commentId);

        // 권한 체크
        const commentSnap = await getDoc(commentRef);
        if (!commentSnap.exists()) throw new Error("댓글을 찾을 수 없습니다.");
        if (commentSnap.data().userId !== userId) throw new Error("수정 권한이 없습니다.");

        await updateDoc(commentRef, {
            text: newText.trim(),
            updatedAt: serverTimestamp()
        });
    },

    /**
     * Register Spot (Store/Post)
     */
    /**
     * Validate Spot Data (Policy Check)
     */
    validateSpot: (data) => {
        const { type, photos } = data;

        // Media Policy Limit Check
        // Store: 10 photos, Post: 3 photos, Product: 1 photo
        const LIMITS = { store: 10, footprint: 3, product: 1 };
        const limit = LIMITS[type] || 3;

        if (photos && photos.length > limit) {
            throw new Error(`PHOTO_LIMIT_EXCEEDED:${limit}`);
        }

        return true;
    },

    /**
     * Register Spot (Store/Post) with Lifecycle
     */
    registerSpot: async (spotData, piUser, environment, spendPpPoints) => {
        // 0. Validate
        SpotService.validateSpot(spotData);

        // 1. Cooldown Check (Spam Prevention)
        if (piUser?.uid) {
            const recentPostsQuery = query(
                collection(db, "spots"),
                where("userId", "==", piUser.uid),
                orderBy("createdAt", "desc"),
                limit(1)
            );
            const recentSnapshot = await getDocs(recentPostsQuery);
            if (!recentSnapshot.empty) {
                const lastPost = recentSnapshot.docs[0].data();
                const lastTime = lastPost.createdAt?.toDate ? lastPost.createdAt.toDate() : new Date(lastPost.createdAt);
                const diff = (Date.now() - lastTime.getTime()) / 1000;
                if (diff < 60) {
                    throw new Error("WAIT_COOLDOWN");
                }
            }
        }

        const { type } = spotData;
        let paymentId = null;
        let txid = null;

        // 🎯 [CHANGED 2026-02-08] 매장 등록 결제 수단 변경: 10 Pi → 티켓 100개
        // 2. Process Payment (Store Only) - REMOVED
        // Pi Payment는 SpotGoMain.js에서 티켓 차감으로 대체됨
        // 호출부에서 spendPpPoints(0, 'store_registration', -100) 호출 필요

        // 3. Call Cloud Function (Using fetch for onRequest function)
        // Note: For footprint, we skip client-side deduction (spendPpPoints) initially
        // because the server handles it atomically.
        const { getAPIUrls } = await import('./api');
        const apiUrls = getAPIUrls(); // 🎯 [FIX] Execute getAPIUrls() to get apiUrls object

        try {
            const response = await fetch(apiUrls.createSpot, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    spotData: {
                        ...spotData,
                        ...(paymentId && { paymentId, txid })
                    },
                    environment,
                    piUser: { uid: piUser.uid, username: piUser.username } // ✅ 필요한 필드만 전달
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const result = await response.json();
            const spotId = result.spotId;

            // 4. Optimistic UI Update (Sync Local State)
            if (type === 'footprint') {
                // 🎯 [Fix] Server handles ticket deduction atomically.
                // Do NOT deduct client-side (removed spendPpPoints call)
            }

            return spotId;
        } catch (error) {
            console.error("SpotService: createSpot failed", error);
            throw error;
        }
    },

    /**
     * Edit Spot via Cloud Function
     */
    editSpot: async (spotId, currentUserId, currentUserName, updateData, environment) => {
        // Policy Check
        if (updateData.photos || updateData.type) {
            // Need 'type' in updateData or need to fetch it? 
            // The validateSpot function currently expects complete data or checks provided fields.
            // Let's skip local validation or fetch spot first if strictly needed.
            // Since server also validates, we can skip or do partial check.
            // But Cloud Function will validate ownership.
        }
        await editSpot(spotId, updateData, { uid: currentUserId, username: currentUserName }, environment);
    },

    /**
     * Delete Spot via Cloud Function
     */
    deleteSpot: async (spotId, currentUserId, currentUserName, environment) => {
        await deleteSpot(spotId, { uid: currentUserId, username: currentUserName }, environment);
    },

    /**
     * Verify Store with Duplicate Prevention
     * 1 Account = 1 Verification per Spot
     */
    verifyStore: async (spotId, piUser, processPiPayment, environment) => {
        if (!piUser) throw new Error("LOGIN_REQUIRED");
        if (!processPiPayment) throw new Error("Payment Provider Missing");

        const spotsCol = getCollectionPath("spots", environment);
        const spotRef = doc(db, spotsCol, spotId);
        const verifyRef = doc(db, spotsCol, spotId, "verifications", piUser.uid);

        // 1. Pre-flight Check (Optimization)
        const verifySnap = await getDoc(verifyRef);
        if (verifySnap.exists()) {
            throw new Error("ALREADY_VERIFIED");
        }

        // 2. Process Payment (Client Side)
        // Note: Assuming 0.1 Pi is fixed
        await processPiPayment(0.1, `Store Verification`, { type: 'verify', spotId: spotId });

        // 3. Transaction: Update DB safely
        await runTransaction(db, async (transaction) => {
            const vDoc = await transaction.get(verifyRef);
            if (vDoc.exists()) throw new Error("ALREADY_VERIFIED");

            // Create Verification Record
            transaction.set(verifyRef, {
                userId: piUser.uid,
                username: piUser.username,
                verifiedAt: serverTimestamp()
            });

            // Increment Count
            transaction.update(spotRef, {
                verificationCount: increment(1),
                lastActiveAt: serverTimestamp()
            });
        });

        return true;
    },

    /**
     * Check if user verified this spot
     */
    checkIsVerified: async (spotId, userId, environment) => {
        if (!userId) return false;
        try {
            const spotsCol = getCollectionPath("spots", environment);
            const ref = doc(db, spotsCol, spotId, "verifications", userId);
            const snap = await getDoc(ref);
            return snap.exists();
        } catch (e) {
            return false;
        }
    },

    /**
     * 🎯 Claim Point Spot Reward (일반 10개/일, 랜드마크 10개/일 별도 제한 — 서버 검증)
     * @param {string} type - 'basic' | 'ad' | 'landmark' (landmark 시 options.landmarkId, spotIndex 필수)
     * @param {string|null} adId - 광고 ID (type 'ad' 또는 'landmark'+광고 시)
     * @param {Object} piUser - 사용자
     * @param {string} environment - 환경
     * @param {Object} options - { landmarkId, spotIndex } (type === 'landmark' 일 때)
     */
    claimPointSpot: async (type, adId, piUser, environment, options = {}) => {
        const apiUrls = getAPIUrls();
        const body = {
            type: type === 'landmark' ? 'landmark' : type,
            adId: adId || undefined,
            userId: piUser.uid,
            environment
        };
        if (type === 'landmark' && (options.landmarkId != null || options.spotIndex != null)) {
            body.landmarkId = options.landmarkId;
            body.spotIndex = options.spotIndex;
        }

        try {
            const res = await fetch(apiUrls.claimPointSpot, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const err = new Error(data.error || data.message || 'Claim failed');
                err.code = data.code;
                throw err;
            }
            return { success: true, amount: data.reward ?? 0, remaining: data.remaining };
        } catch (e) {
            console.error('[SpotService] claimPointSpot failed:', e);
            throw e;
        }
    },

    /**
     * 🎯 [NEW] Create Spot (매장/포스트 등록) - 티켓 결제 방식
     * @param {Object} spotData - 스팟 데이터 (title, content, type, coordinates, etc.)
     * @param {Object} piUser - 사용자 정보
     * @param {string} environment - 환경 정보
     */
    createSpot: async (spotData, piUser, environment) => {
        if (!piUser) throw new Error("로그인이 필요합니다.");
        if (!spotData || !spotData.type) throw new Error("스팟 타입이 필요합니다.");

        try {
            const apiUrls = getAPIUrls();

            // Call createSpot Cloud Function
            const response = await fetch(apiUrls.createSpot, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    spotData,
                    piUser,
                    environment
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || error.error || 'Failed to create spot');
            }

            const result = await response.json();
            return result;
        } catch (e) {
            console.error('[SpotService] createSpot failed:', e);
            throw e;
        }
    }
};
